import { translate } from '../../../helper/translate';
import { useEffect, useState } from 'react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { setPageCacheHeaders } from '../../../helper/cache-control';
import { useRouter } from 'next/router';
import {
  GetCaseStudyRes,
  getCaseStudyList,
  getArticleList,
  getVideoList,
  getWebinarList,
  getWhitePaperList,
  getEditable,
} from '../../../helper';
import { CaseStudyRes, ResourcesRes } from '../../../typescript/pages';
import styles from '../../../styles/WhitePaper.module.css';
import HeroBanner from '../../../components/banners/hero-banner';
import HeroTitle from '../../../components/ui/hero-title';
import ResourceAside from '../../../components/specialized/resource-aside';
import * as HtmlReactParser from 'html-react-parser';
const parse = HtmlReactParser.default || HtmlReactParser;
import {
  HeroBanner as HeroBannerProp,
  HeroBannerStyleButton,
  HeroTitle as HeroTitleProp,
} from '../../../typescript/component';
import { getRandomItems } from '../../../helper/utils';
import { getSimilarResourcesHybrid } from '../../../helper/algoliaSimilarity';
import { SEO } from '../../../helper/seo';
import { cleanHtml } from '../../../sdk/utils';
import { HtmlBlock } from '../../../components/media/html-block';

// Live Preview imports
import ContentstackLivePreview from '@contentstack/live-preview-utils';
import { initLivePreview, initializeContentStackNewSdk } from '../../../contentstack-sdk/utils';
import { usePageState } from '../../../context/pageContext';
import { getLocales } from '../../../sdk/i18n';

export const getServerSideProps: GetServerSideProps<{
  caseStudy: CaseStudyRes;
  randomResources: ResourcesRes[];
  locale: string;
  availableLocales: string[];
  source: 'algolia' | 'random' | 'mixed';
}> = async ({ params, locale, locales, res }) => {
  const availableLocales = [];
  const lang = locale || 'en-us';

  const caseStudyId = params?.caseStudyId as string;
  const entryUrl = `/resources/case-study/${caseStudyId}`;

  try {
    const caseStudy = await GetCaseStudyRes({
      entryUrl: entryUrl,
      locale: lang
    });
    if (!caseStudy) return { notFound: true };

    if (locales && locales.length) {
      for (let l of locales) {
        const localeResource = await GetCaseStudyRes({
          entryUrl: entryUrl,
          locale: l
        });
  
        if (localeResource) {
          availableLocales.push(l);
        }
      }
    }

    // Try to get similar resources from Algolia first
    let similarResources: ResourcesRes[] = [];
    let source: 'algolia' | 'random' | 'mixed' = 'random';

    try {
      const result = await getSimilarResourcesHybrid(
        {
          uid: caseStudy.uid,
          title: caseStudy.title,
          description: caseStudy.description || caseStudy.summary,
          keywords: caseStudy.global_field?.keywords
            ? caseStudy.global_field.keywords.split(',').map(k => k.trim())
            : [],
          content: caseStudy.content,
          type: 'Case Study',
        },
        {
          locale: lang,
          maxResults: 3,
          excludeCurrentId: caseStudy.url, // Use URL as objectID
        },
      );

      similarResources = result.resources;
      source = result.source;
    } catch (error) {
      // Silently fail and use fallback
    }

    // Fallback to random resources if Algolia similarity fails or returns no results
    if (similarResources.length === 0) {
      // Get random resources from all types, not just case studies
      const [articles, caseStudies, videos, webinars, whitepapers] = await Promise.all([
        getArticleList(lang),
        getCaseStudyList(lang),
        getVideoList(lang),
        getWebinarList(lang),
        getWhitePaperList(lang),
      ]);

      // Flatten and combine all resource types
      const allResources = [
        ...articles.flat(),
        ...caseStudies,
        ...videos,
        ...webinars,
        ...whitepapers,
      ];

      similarResources = getRandomItems(allResources, 3);
      source = 'random';
    }

    setPageCacheHeaders(res);

    return {
      props: {
        caseStudy,
        randomResources: similarResources,
        locale: lang,
        availableLocales,
        source,
      },
    };
  } catch {
    return { notFound: true };
  }
};

export default function CaseStudyPage({
  caseStudy,
  randomResources,
  locale,
  availableLocales,
  source,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [, setAvailableLocales] = usePageState('availableLocales');
  const [isPreview, setPreview] = useState(false);
  const [data, setData] = useState(caseStudy);
  const router = useRouter();
  if (router.isFallback) return <div>Loading...</div>;

  // Keep the shared page context in sync with this entry's locales, and clear it
  // on unmount so other pages don't inherit a stale list. Depending on the prop
  // matters because Next reuses this component when navigating between two
  // entries of the same type, so a mount-only effect would never re-run.
  useEffect(() => {
    setAvailableLocales(availableLocales ?? []);
    return () => setAvailableLocales([]);
  }, [availableLocales]);

  const heroBannerData: HeroBannerProp = {
    banner_eyebrow: translate('case_study', locale).toUpperCase(),
    banner_title: data.title,
    banner_title_alt: '',
    display_options: {
      align_image: 'Right',
      align_text: 'Left',
      background_color: 'Light',
      breadcrumb: true,
      show_button: false,
      split_50_50: true,
    },
    header_tag_type: 'H1',
    banner_media: data.featured_image,
    banner_media_alt: data.featured_image_alt,
    button: {
      link: '',
      title: '',
      style: HeroBannerStyleButton.SOLID,
    },
    anchor: '',
  };

  const heroTitleData: HeroTitleProp = {
    align_text: 'Left',
    bottom_border: true,
    content: '',
    eyebrow: translate('case_study', locale).toUpperCase(),
    header_tag_type: 'H1',
    show_breadcrumb: true,
    title: data.title,
  };

  const editableFields = {
    title: getEditable(data, 'title'),
    banner_title: getEditable(data, 'title'),
    banner_media: getEditable(data, 'featured_image'),
    banner_media_alt: getEditable(data, 'featured_image_alt'),
    description: getEditable(data, 'content')
  }

  const getContent = async ({ stack }: {stack: any}) => {
    const page = await GetCaseStudyRes({
      entryUrl: window.location.pathname,
      locale: locale,
      stackInstance: stack,
      clientSide: true
    });

    // Store data into state so we can dynamically change update the fields
    setData(page);
  };

  useEffect(() => {
    setData(caseStudy);
  }, [caseStudy]);

  useEffect(() => {
    const stack = initializeContentStackNewSdk({ preview: true });

    initLivePreview({stackInstance: stack});
    ContentstackLivePreview.onEntryChange(() => {
      
      // It's loaded from the live preview so we want to finish up the setup
      if (stack.config.live_preview && stack.config.live_preview.live_preview) {
        setPreview(true);
        getContent({stack});
      }
    });
  }, []);

  return (
    <>
      <SEO
        meta_title={data?.global_field?.meta_title}
        meta_description={data?.global_field?.meta_description}
        keywords={data?.global_field?.keywords}
        share_image={data?.featured_image?.url}
        currentLocale={locale}
        baseUrl={process.env.NEXT_PUBLIC_HOSTED_URL || ''}
        contentType="CaseStudy"
        publishDate={data?.updated_at || undefined}
        modifiedDate={data?.updated_at || undefined}
      />
      <div className={styles.outerWrapper}>
        {data.featured_image?.url ? (
          <HeroBanner key="caseStudyHeroBanner" hero_banner={heroBannerData} editable={isPreview ? true : false} editableFields={editableFields} />
        ) : (
          <HeroTitle key="caseStudyHeroTitle" hero_title={heroTitleData} />
        )}
        <div className={styles.moduleWrapper}>
          <div className={styles.root}>
            <section className={styles.virtualTour}>
              <HtmlBlock html={data.content} className={styles.description} editable={getEditable(data, 'content')} />
            </section>
            <div className={styles.divider} />
            <div className={styles.taxonomyWrapper}>
              <ResourceAside
                key={data.uid}
                relatedResources={randomResources}
                type="Case Study"
                source={source}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
